import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../lib/axios';
import { euro, dateFmt } from '../lib/format';

function IncomeSectionRows({ s, negative }: { s: any; negative?: boolean }) {
  if (!s || !s.lines || s.lines.length === 0) {
    return null;
  }
  const sign = negative ? -1 : 1;
  return (
    <>
      <tr className="bg-surface-sunken">
        <td colSpan={2} className="py-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">
          {s.title}
        </td>
      </tr>
      {s.lines.map((l: any) => (
        <tr key={l.accountNumber}>
          <td>
            {l.accountNumber} - {l.label}
          </td>
          <td className="num text-ink-soft">{euro(sign * Number(l.amount))}</td>
        </tr>
      ))}
      <tr>
        <td className="font-medium">Total</td>
        <td className="num font-medium">{euro(sign * Number(s.total))}</td>
      </tr>
    </>
  );
}

function ResultRow({ label, value }: { label: string; value: number }) {
  return (
    <tr className="border-y border-line bg-surface-raised">
      <td className="font-semibold">{label}</td>
      <td className="num font-semibold">{euro(Number(value))}</td>
    </tr>
  );
}

const hasLines = (s: any) => !!(s && s.lines && s.lines.length > 0);

function today() {
  return new Date().toISOString().slice(0, 10);
}
function firstOfYear() {
  return `${new Date().getFullYear()}-01-01`;
}

type Doc = 'journal' | 'ledger' | 'balance' | 'income' | 'balanceSheet';
const DOCS: Doc[] = ['journal', 'ledger', 'balance', 'income', 'balanceSheet'];

export default function Reports() {
  const { t } = useTranslation();
  const [doc, setDoc] = useState<Doc>('balance');
  const [start, setStart] = useState(firstOfYear());
  const [end, setEnd] = useState(today());
  const [account, setAccount] = useState('');

  const balance = useQuery({
    queryKey: ['trial-balance', start, end],
    queryFn: () => api.get('/reports/trial-balance', { params: { start, end } }).then((r) => r.data),
    enabled: doc === 'balance',
  });

  const journal = useQuery({
    queryKey: ['journal-book', start, end],
    queryFn: () =>
      api
        .get('/entries', { params: { page: 0, size: 1000, startDate: start, endDate: end } })
        .then((r) => r.data),
    enabled: doc === 'journal',
  });

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get('/accounts').then((r) => r.data),
    enabled: doc === 'ledger',
  });

  const ledger = useQuery({
    queryKey: ['general-ledger', account, start, end],
    queryFn: () =>
      api.get(`/reports/general-ledger/${account}`, { params: { start, end } }).then((r) => r.data),
    enabled: doc === 'ledger' && !!account,
  });

  const income = useQuery({
    queryKey: ['income-statement', start, end],
    queryFn: () => api.get('/reports/income-statement', { params: { start, end } }).then((r) => r.data),
    enabled: doc === 'income',
  });

  const balanceSheet = useQuery({
    queryKey: ['balance-sheet', end],
    queryFn: () => api.get('/reports/balance-sheet', { params: { date: end } }).then((r) => r.data),
    enabled: doc === 'balanceSheet',
  });

  const download = async (fmt: 'excel' | 'pdf') => {
    const res = await api.get(`/reports/trial-balance/export/${fmt}`, {
      params: { start, end },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fmt === 'excel' ? 'balance.xlsx' : 'balance.pdf';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async (path: string, params: Record<string, string>, filename: string) => {
    const res = await api.get(path, { params, responseType: 'blob' });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const journalRows = useMemo(() => {
    const rows = journal.data?.content ?? journal.data ?? [];
    return [...rows].sort((a: any, b: any) =>
      a.entryDate === b.entryDate
        ? String(a.number).localeCompare(String(b.number))
        : a.entryDate.localeCompare(b.entryDate),
    );
  }, [journal.data]);

  return (
    <div>
      <header className="mb-6">
        <p className="eyebrow">{t('nav.reports')}</p>
        <h1 className="page-title mt-1">{t('reports.title')}</h1>
      </header>

      <div className="mb-5 inline-flex flex-wrap gap-1 rounded-xl border border-line bg-surface-raised p-1">
        {DOCS.map((d) => (
          <button
            key={d}
            onClick={() => setDoc(d)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              doc === d
                ? 'bg-surface text-ink shadow-sm'
                : 'text-ink-muted hover:text-ink'
            }`}
          >
            {t(`reports.doc.${d}`)}
          </button>
        ))}
      </div>

      <div className="card mb-6 flex flex-wrap items-end gap-4 p-5">
        {doc === 'balanceSheet' ? (
          <div>
            <label className="field-label">{t('reports.atDate')}</label>
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="input"
            />
          </div>
        ) : (
          <>
            <div>
              <label className="field-label">{t('reports.from')}</label>
              <input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="field-label">{t('reports.to')}</label>
              <input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="input"
              />
            </div>
          </>
        )}

        {doc === 'ledger' && (
          <div>
            <label className="field-label">{t('field.account')}</label>
            <input
              list="ledger-accounts"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              placeholder={t('reports.selectAccount')}
              className="input tnum"
            />
            <datalist id="ledger-accounts">
              {(accounts.data ?? []).map((a: any) => (
                <option key={a.number} value={a.number}>
                  {a.number} - {a.label}
                </option>
              ))}
            </datalist>
          </div>
        )}

        {doc === 'balance' && (
          <div className="ml-auto flex gap-2">
            <button onClick={() => download('excel')} className="btn btn-secondary">
              {t('reports.exportExcel')}
            </button>
            <button onClick={() => download('pdf')} className="btn btn-secondary">
              {t('reports.exportPdf')}
            </button>
          </div>
        )}

        {doc === 'income' && (
          <div className="ml-auto flex gap-2">
            <button
              onClick={() =>
                downloadPdf('/reports/income-statement/export/pdf', { start, end }, 'compte-de-resultat.pdf')
              }
              className="btn btn-secondary"
            >
              {t('reports.exportPdf')}
            </button>
          </div>
        )}

        {doc === 'balanceSheet' && (
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => downloadPdf('/reports/balance-sheet/export/pdf', { date: end }, 'bilan.pdf')}
              className="btn btn-secondary"
            >
              {t('reports.exportPdf')}
            </button>
          </div>
        )}
      </div>

      {doc === 'balance' && (
        <div className="card p-3 overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t('field.account')}</th>
                <th>{t('field.label')}</th>
                <th className="num">{t('field.debit')}</th>
                <th className="num">{t('field.credit')}</th>
                <th className="num">{t('field.balance')}</th>
              </tr>
            </thead>
            <tbody>
              {(balance.data ?? []).map((l: any) => (
                <tr key={l.accountNumber}>
                  <td data-label={t('field.account')} className="font-medium tnum">{l.accountNumber}</td>
                  <td data-label={t('field.label')}>{l.label}</td>
                  <td data-label={t('field.debit')} className="num text-ink-soft">{euro(l.totalDebit)}</td>
                  <td data-label={t('field.credit')} className="num text-ink-soft">{euro(l.totalCredit)}</td>
                  <td data-label={t('field.balance')} className="num font-medium">{euro(l.balance)}</td>
                </tr>
              ))}
              {(balance.data ?? []).length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-ink-muted">
                    {t('reports.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {doc === 'journal' && (
        <div className="card p-3 overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t('field.date')}</th>
                <th>{t('field.number')}</th>
                <th>{t('field.account')}</th>
                <th>{t('field.label')}</th>
                <th className="num">{t('field.debit')}</th>
                <th className="num">{t('field.credit')}</th>
              </tr>
            </thead>
            <tbody>
              {journalRows.map((e: any) =>
                (e.lines ?? []).map((l: any, i: number) => (
                  <tr key={`${e.id}-${l.id}`}>
                    <td data-label={t('field.date')} className="whitespace-nowrap text-ink-soft">
                      {i === 0 ? dateFmt(e.entryDate) : ''}
                    </td>
                    <td data-label={t('field.number')} className="tnum text-ink-soft">{i === 0 ? e.number : ''}</td>
                    <td data-label={t('field.account')} className="tnum">{l.accountNumber}</td>
                    <td data-label={t('field.label')}>{l.label || e.label}</td>
                    <td data-label={t('field.debit')} className="num text-ink-soft">{l.debit ? euro(l.debit) : ''}</td>
                    <td data-label={t('field.credit')} className="num text-ink-soft">{l.credit ? euro(l.credit) : ''}</td>
                  </tr>
                )),
              )}
              {journalRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-ink-muted">
                    {t('reports.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {doc === 'ledger' && (
        <div className="card p-3 overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t('field.date')}</th>
                <th>{t('field.label')}</th>
                <th className="num">{t('field.debit')}</th>
                <th className="num">{t('field.credit')}</th>
                <th className="num">{t('field.balance')}</th>
              </tr>
            </thead>
            <tbody>
              {(ledger.data?.movements ?? []).map((m: any, i: number) => (
                <tr key={i}>
                  <td data-label={t('field.date')} className="whitespace-nowrap text-ink-soft">{dateFmt(m.date)}</td>
                  <td data-label={t('field.label')}>{m.label}</td>
                  <td data-label={t('field.debit')} className="num text-ink-soft">{m.debit ? euro(m.debit) : ''}</td>
                  <td data-label={t('field.credit')} className="num text-ink-soft">{m.credit ? euro(m.credit) : ''}</td>
                  <td data-label={t('field.balance')} className="num font-medium">{euro(m.balance)}</td>
                </tr>
              ))}
              {(!account || (ledger.data?.movements ?? []).length === 0) && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-ink-muted">
                    {account ? t('reports.empty') : t('reports.selectAccount')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {doc === 'income' && income.data && (
        <div className="card overflow-x-auto p-3">
          <table className="table">
            <thead>
              <tr>
                <th>{t('field.account')}</th>
                <th className="num">{t('reports.currentPeriod')}</th>
              </tr>
            </thead>
            <tbody>
              <IncomeSectionRows s={income.data.operatingIncome} />
              <IncomeSectionRows s={income.data.operatingExpenses} negative />
              <ResultRow label={t('reports.operatingResult')} value={income.data.operatingResult} />

              <IncomeSectionRows s={income.data.financialIncome} />
              <IncomeSectionRows s={income.data.financialExpenses} negative />
              {(hasLines(income.data.financialIncome) || hasLines(income.data.financialExpenses)) && (
                <ResultRow label={t('reports.financialResult')} value={income.data.financialResult} />
              )}

              <IncomeSectionRows s={income.data.exceptionalIncome} />
              <IncomeSectionRows s={income.data.exceptionalExpenses} negative />
              {(hasLines(income.data.exceptionalIncome) ||
                hasLines(income.data.exceptionalExpenses)) && (
                <ResultRow
                  label={t('reports.exceptionalResult')}
                  value={income.data.exceptionalResult}
                />
              )}

              <IncomeSectionRows s={income.data.volunteerResources} />
              <IncomeSectionRows s={income.data.volunteerUses} negative />

              <ResultRow
                label={Number(income.data.result) >= 0 ? t('reports.surplus') : t('reports.deficit')}
                value={income.data.result}
              />
            </tbody>
          </table>
        </div>
      )}

      {doc === 'balanceSheet' && balanceSheet.data && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="card overflow-x-auto p-3">
            <h3 className="px-2 py-2 font-display text-base font-semibold">{t('reports.assets')}</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>{t('field.account')}</th>
                  <th className="num">{t('reports.gross')}</th>
                  <th className="num">{t('reports.depreciation')}</th>
                  <th className="num">{t('reports.net')}</th>
                </tr>
              </thead>
              <tbody>
                {balanceSheet.data.assets.map((s: any) => (
                  <Fragment key={s.title}>
                    <tr className="bg-surface-sunken">
                      <td
                        colSpan={4}
                        className="py-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted"
                      >
                        {s.title}
                      </td>
                    </tr>
                    {s.lines.map((l: any) => (
                      <tr key={l.accountNumber}>
                        <td data-label={t('field.account')}>
                          {l.accountNumber} - {l.label}
                        </td>
                        <td data-label={t('reports.gross')} className="num text-ink-soft">{euro(l.gross)}</td>
                        <td data-label={t('reports.depreciation')} className="num text-ink-soft">
                          {Number(l.depreciation) ? euro(l.depreciation) : ''}
                        </td>
                        <td data-label={t('reports.net')} className="num font-medium">{euro(l.net)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="font-medium">Total {s.title}</td>
                      <td className="num font-medium">{euro(s.grossTotal)}</td>
                      <td className="num font-medium">{euro(s.depreciationTotal)}</td>
                      <td className="num font-medium">{euro(s.netTotal)}</td>
                    </tr>
                  </Fragment>
                ))}
                <tr className="border-t border-line bg-surface-raised">
                  <td className="font-semibold">{t('reports.totalAssets')}</td>
                  <td className="num font-semibold">{euro(balanceSheet.data.assetsGross)}</td>
                  <td className="num font-semibold">{euro(balanceSheet.data.assetsDepreciation)}</td>
                  <td className="num font-semibold">{euro(balanceSheet.data.assetsNet)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="card overflow-x-auto p-3">
            <h3 className="px-2 py-2 font-display text-base font-semibold">
              {t('reports.liabilities')}
            </h3>
            <table className="table">
              <thead>
                <tr>
                  <th>{t('field.account')}</th>
                  <th className="num">{t('reports.net')}</th>
                </tr>
              </thead>
              <tbody>
                {balanceSheet.data.liabilities.map((s: any) => (
                  <Fragment key={s.title}>
                    <tr className="bg-surface-sunken">
                      <td
                        colSpan={2}
                        className="py-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted"
                      >
                        {s.title}
                      </td>
                    </tr>
                    {s.lines.map((l: any) => (
                      <tr key={l.accountNumber}>
                        <td data-label={t('field.account')}>
                          {l.accountNumber} - {l.label}
                        </td>
                        <td data-label={t('reports.net')} className="num text-ink-soft">{euro(l.net)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="font-medium">Total {s.title}</td>
                      <td className="num font-medium">{euro(s.netTotal)}</td>
                    </tr>
                  </Fragment>
                ))}
                <tr className="border-t border-line bg-surface-raised">
                  <td className="font-semibold">{t('reports.totalLiabilities')}</td>
                  <td className="num font-semibold">{euro(balanceSheet.data.liabilitiesTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
